import React, { useEffect, useState } from "react";
import ContentLayout from "../../layout/contentLayout";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import { Grid, TextField, Typography } from "@mui/material";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import StackKit from "@/shared/components/kit/stack";
import BoxKit from "@/shared/components/kit/box";
import CachedOutlinedIcon from "@mui/icons-material/CachedOutlined";
import Content from "./content";
import StackedImageSlider from "@/shared/components/common/slider";
import CustomTable from "@/shared/components/common/table";
import CustomButton from "@/shared/components/kit/button";
import CardKit from "@/shared/components/kit/card";
import {
  numberSeparator,
  toPersianNumber,
} from "@/hooks/useThousandsSeparator";
import { useNavigate } from "react-router";
import CardBoxLayout from "../../layout/cardBoxLayout";
import {
  fetchListActivePazirandeBankAccounts,
  fetchListGetBalancePazirande,
  fetchListLastWithdrawRequestPazirande,
} from "@/api/merchant/request.get";
import { useQuery } from "@tanstack/react-query";
import moment from "jalali-moment";
import { mapBankTitleToImageName } from "../walletCardManagement/data";

/**
 * WithdrawWallet
 *
 * کامپوننت React برای نمایش و مدیریت برداشت از کیف پول نقدی کاربران (Pazirande).
 * این کامپوننت شامل موارد زیر است:
 * - نمایش موجودی کیف پول نقدی به همراه دکمه بروزرسانی
 * - نمایش کارت‌های بانکی فعال کاربر با اسلایدر تصویری
 * - جدول آخرین درخواست‌های برداشت با قابلیت قالب‌بندی اعداد و تاریخ‌ها
 * - قابلیت انتخاب کارت بانکی برای برداشت
 * - لینک به گزارش ها کامل برداشت از کیف پول
 *
 * ویژگی‌ها:
 * - استفاده از React Query برای واکشی داده‌ها (`balance`, `withdraw requests`, `bank accounts`)
 * - قالب‌بندی اعداد و تاریخ‌ها با اعداد فارسی و جداکننده هزارگان
 * - مدیریت انتخاب کارت بانکی و اسلایدر تصاویر
 *
 * @returns JSX.Element
 *
 * @example
 * ```tsx
 * <WithdrawWallet />
 * ```
 */
const WithdrawWallet = () => {
  const navigation = useNavigate();
  const [sliderIndex, setSliderIndex] = useState(0);
  const [selected, setSelected] = useState<any>();

  /** واکشی موجودی کیف پول Pazirande */
  const {data: dataBalancePazirande,isFetched,isLoading} = useQuery({
    queryKey: ["BalancePazirande-data"],
    queryFn: fetchListGetBalancePazirande,
  });

  /** واکشی آخرین درخواست‌های برداشت Pazirande */
  const {data: dataWithdrawRequestPazirande,isFetched: isFetchingWithdrawRequestPazirande,isLoading: isLoadingWithdrawRequestPazirande,refetch} = useQuery({
    queryKey: ["WithdrawRequestPazirande-data"],
    queryFn: fetchListLastWithdrawRequestPazirande,
  });

  /** سربرگ‌های جدول درخواست‌های برداشت */
  const dataHeader = [
    { id: "rowId", title: "ردیف" },
    { id: "price", title: "مبلغ  ریال" },
    { id: "created_at", title: "ساعت و تاریخ درخواست" },
    { id: "source_card", title: "مبدا تراکنش" },
    { id: "target_card", title: "مقصد تراکنش" },
    { id: "status", title: "وضعیت درخواست" },
    { id: "detaile", title: "توضیحات" },
  ];

  /** قالب‌بندی داده‌های درخواست‌های برداشت برای نمایش در جدول */
  const newData = dataWithdrawRequestPazirande?.withdraws.map(
    (item: any, index: number) => ({
      ...item,
      rowId: toPersianNumber(index + 1),
      price: numberSeparator(item?.price),
      status: (
        <StackKit direction={"row"} gap={1}>
          <TypoGraphyKit variant="body1">
            {item?.status === "pending" ? "درحال بررسی" : "-"}
          </TypoGraphyKit>
        </StackKit>
      ),
      created_at: (
        <TypoGraphyKit>
          {toPersianNumber(`${moment(item.created_at).locale("fa").format("HH:MM")} ${moment(item.created_at).locale("fa").format("jYYYY/jMM/jDD")} `)}
        </TypoGraphyKit>
      ),
      source_card: (
        <TypoGraphyKit sx={{ direction: "ltr" }}>
          {toPersianNumber(item.source_card)}
        </TypoGraphyKit>
      ),
      target_card: (
        <TypoGraphyKit sx={{ direction: "ltr" }}>
          {toPersianNumber(item.target_card)}
        </TypoGraphyKit>
      ),
    })
  );
  const fakeData = newData;

  /** واکشی کارت‌های بانکی فعال کاربر */
  const { data: dataListPazirandeClubs } = useQuery({
    queryKey: ["ListPazirandeClubsWithdrawWallet-data"],
    queryFn: fetchListActivePazirandeBankAccounts,
  });
  const bankAccounts = dataListPazirandeClubs?.data ?? [];

  /** آماده‌سازی داده برای اسلایدر تصاویر کارت‌های بانکی */
  const itemsImg = bankAccounts?.map((item: any) => {
    const imageName = mapBankTitleToImageName(item.title);
    return {
      id: item.id,
      url: `/bank-cards/${imageName}.jpg`,
      title: item.title,
      footer: item.card,
    };
  });

  /** انتخاب اولین کارت بانکی در بارگذاری اولیه */
  useEffect(() => {
    if (itemsImg && itemsImg.length > 0 && !selected) {
      setSliderIndex(itemsImg[0].id);
      setSelected(itemsImg[0].id);
    }
  }, [itemsImg, selected]);

  return (
    <StackKit sx={{ width: "100%" }}>
      {/* بخش بالای صفحه: اطلاعات کیف پول و اسلایدر کارت بانکی */}
      <StackKit direction={{ xs: "column-reverse", md: "row" }} alignItems={{ xs: "flex-start", md: "center" }} sx={{ minHeight: "400px", gap: { xs: 3, md: 0 } }}>
        {/* سمت چپ: اطلاعات کیف پول و محتوا */}
        <StackKit sx={{ width: { xs: "100%", md: "70%" }, height: "100%" }} gap={2}>
          {/* عنوان و آیکون */}
          <StackKit direction={"row"} alignItems={{ xs: "flex-start", sm: "center" }} gap={1} flexWrap="wrap">
            <TypoGraphyKit variant="h6">
              <AccountBalanceWalletOutlinedIcon />
            </TypoGraphyKit>
            <TypoGraphyKit variant="h5">برداشت از کیف پول</TypoGraphyKit>
          </StackKit>

          {/* نمایش موجودی کیف پول */}
          <StackKit direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent={{ xs: "flex-start", sm: "space-between" }} gap={2} flexWrap="wrap">
            <TypoGraphyKit variant="body1">
              موجودی کل کیف پول نقدی در حال حاضر
            </TypoGraphyKit>

            <StackKit direction={{ xs: "column", sm: "row" }} gap={2}>
              <TypoGraphyKit variant="h6">
                {numberSeparator(dataBalancePazirande?.balance)} ریال
              </TypoGraphyKit>
            </StackKit>
          </StackKit>

          {/* کامپوننت محتوا با امکان انتخاب کارت */}
          <Content dataBalancePazirande={dataBalancePazirande} setSelected={setSelected} selected={selected} refetchDataList={refetch}/>
        </StackKit>

        {/* سمت راست: اسلایدر کارت بانکی */}
        <StackKit sx={{ width: { xs: "100%", md: "30%" }, mt: { xs: 3, md: 0 } }}>
          {dataListPazirandeClubs?.data?.length === 0 ? (
            <StackKit sx={{ width: "100%" }} alignItems={"center"}>
              <StackKit sx={{width: "238px",height: "160px",bgcolor: "#2E2E2E",cursor: "pointer",}} justifyContent={"center"} alignItems={"center"}>
                <TypoGraphyKit variant="body1">کارت بانکی وجود ندارد</TypoGraphyKit>
              </StackKit>
              <StackKit direction={"row"} onClick={() => navigation("/wallet-reports/add-shetab-card")} justifyContent={"center"} sx={{ cursor: "pointer" }}/>
            </StackKit>
          ) : (
            <StackKit sx={{ width: "100%" }}>
              <StackedImageSlider
                activeIndex={sliderIndex}
                onSlideChange={(swiper) => {
                  const index = swiper.realIndex;
                  const selectedItem = itemsImg && itemsImg[index];
                  setSelected(`${selectedItem?.id}`);
                }}
                items={itemsImg!}
                renderItem={(item: any) => (
                  <div style={{position: "relative",width: "100%",maxWidth: "300px",margin: "0 auto",}}>
                    <img src={item.url} alt="کارت" style={{width: "100%",height: "100%",objectFit: "cover",borderRadius: "12px",}}/>
                    <div style={{position: "absolute",top: "50%",left: "50%",transform: "translate(-50%, -50%)",backgroundColor: "rgba(0, 0, 0, 0.5)",color: "white",padding: "8px 12px",borderRadius: "8px",fontSize: "14px",}}>
                      {item.title}
                    </div>
                    <div style={{position: "absolute",bottom: "12px",right: "12px",backgroundColor: "rgba(255, 255, 255, 0.8)",color: "#333",padding: "6px 10px",borderRadius: "6px",fontSize: "12px",}}>
                      {item.footer}
                    </div>
                  </div>
                )}
              />
            </StackKit>
          )}
        </StackKit>
      </StackKit>

      {/* جدول درخواست‌های برداشت */}
      <CardBoxLayout sx={{ bgcolor: "#434343", width: "100%", mt: { xs: 3, md: 5 } }}>
        <StackKit justifyContent={"space-between"} sx={{ height: "100%", gap: 2 }}>
          <Grid container sx={{ width: "100%" }} spacing={2}>
            <Grid size={{xs:12}}>
              <StackKit sx={{ width: "100%", alignItems: "start" }}>
                <TypoGraphyKit variant="h6">آخرین درخواست های برداشت از موجودی</TypoGraphyKit>
              </StackKit>
              <StackKit direction={{ xs: "column", sm: "row" }} justifyContent={"end"} sx={{ width: "100%" }} gap={2}>
                <TextField sx={{ root: { bgcolor: "#323232", width: "100%" } }} />
                <CustomButton sx={{ padding: "10px 25px", width: { xs: "100%", sm: "auto" } }}>
                  <TypoGraphyKit variant="body1">جستجو</TypoGraphyKit>
                </CustomButton>
              </StackKit>
            </Grid>

            <Grid size={{xs:12}}>
              <CustomTable dataHeader={dataHeader} dataTbody={fakeData} />
            </Grid>
          </Grid>

          <StackKit sx={{ width: "100%" }} justifyContent={"end"} direction={"row"}>
            <CustomButton onClick={() => navigation("/wallet-balance-reports")} 
            sx={{bgcolor: "transparent",border: "1px dashed #EB6440",width: "fit-content","&:hover": { bgcolor: "transparent", borderColor: "#EB6440" },}}>
              <TypoGraphyKit variant="body1" sx={{ color: "#EB6440" }}>
                مشاهده گزارش ها برداشت از کیف پول
              </TypoGraphyKit>
            </CustomButton>
          </StackKit>
        </StackKit>
      </CardBoxLayout>
    </StackKit>
  );
};

export default WithdrawWallet;
